import { useState, useRef, useEffect } from "react";
import { cities, plans, planLabels, statuses } from "@/mocks/superadmin/tenants";

interface TenantFiltersProps {
  search: string;
  onSearchChange: (val: string) => void;
  planFilter: string;
  onPlanChange: (val: string) => void;
  statusFilter: string;
  onStatusChange: (val: string) => void;
  cityFilter: string;
  onCityChange: (val: string) => void;
  totalResults: number;
  filteredCount: number;
}

export default function TenantFilters({
  search,
  onSearchChange,
  planFilter,
  onPlanChange,
  statusFilter,
  onStatusChange,
  cityFilter,
  onCityChange,
  totalResults,
  filteredCount,
}: TenantFiltersProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const statusLabels: Record<string, { label: string; dot: string }> = {
    active: { label: "Active", dot: "bg-emerald-500" },
    trial: { label: "Trial", dot: "bg-amber-400" },
    churned: { label: "Churned", dot: "bg-red-400" },
    suspended: { label: "Suspended", dot: "bg-slate-400" },
  };

  return (
    <div className="space-y-3" ref={filterRef}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 w-full">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
          <input
            type="text"
            placeholder="Search by restaurant name, owner, or city..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
          {search && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <button
              onClick={() => setOpenDropdown(openDropdown === "plan" ? null : "plan")}
              className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md border transition-colors whitespace-nowrap cursor-pointer ${
                planFilter
                  ? "border-primary-300 bg-primary-50 text-primary-700"
                  : "border-background-200 bg-background-50 text-foreground-600 hover:border-background-300"
              }`}
            >
              {planFilter ? planLabels[planFilter] ?? planFilter : "All Plans"}
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${openDropdown === "plan" ? "rotate-180" : ""}`}></i>
            </button>
            {openDropdown === "plan" && (
              <div className="absolute top-full mt-1 left-0 w-40 bg-background-50 border border-background-200 rounded-md shadow-sm py-1 z-20">
                <button
                  onClick={() => { onPlanChange(""); setOpenDropdown(null); }}
                  className="w-full text-left px-3 py-2 text-sm text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  All Plans
                </button>
                {plans.map((p) => (
                  <button
                    key={p}
                    onClick={() => { onPlanChange(p); setOpenDropdown(null); }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      planFilter === p ? "bg-primary-50 text-primary-700 font-medium" : "text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    {planLabels[p] ?? p}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setOpenDropdown(openDropdown === "status" ? null : "status")}
              className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md border transition-colors whitespace-nowrap cursor-pointer ${
                statusFilter
                  ? "border-primary-300 bg-primary-50 text-primary-700"
                  : "border-background-200 bg-background-50 text-foreground-600 hover:border-background-300"
              }`}
            >
              {statusFilter ? statusLabels[statusFilter]?.label : "All Status"}
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${openDropdown === "status" ? "rotate-180" : ""}`}></i>
            </button>
            {openDropdown === "status" && (
              <div className="absolute top-full mt-1 left-0 w-40 bg-background-50 border border-background-200 rounded-md shadow-sm py-1 z-20">
                <button
                  onClick={() => { onStatusChange(""); setOpenDropdown(null); }}
                  className="w-full text-left px-3 py-2 text-sm text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  All Status
                </button>
                {statuses.map((s) => (
                  <button
                    key={s}
                    onClick={() => { onStatusChange(s); setOpenDropdown(null); }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                      statusFilter === s ? "bg-primary-50 text-primary-700 font-medium" : "text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${statusLabels[s]?.dot}`}></span>
                    {statusLabels[s]?.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setOpenDropdown(openDropdown === "city" ? null : "city")}
              className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md border transition-colors whitespace-nowrap cursor-pointer ${
                cityFilter
                  ? "border-primary-300 bg-primary-50 text-primary-700"
                  : "border-background-200 bg-background-50 text-foreground-600 hover:border-background-300"
              }`}
            >
              {cityFilter || "All Cities"}
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${openDropdown === "city" ? "rotate-180" : ""}`}></i>
            </button>
            {openDropdown === "city" && (
              <div className="absolute top-full mt-1 left-0 w-40 bg-background-50 border border-background-200 rounded-md shadow-sm py-1 max-h-48 overflow-y-auto z-20">
                <button
                  onClick={() => { onCityChange(""); setOpenDropdown(null); }}
                  className="w-full text-left px-3 py-2 text-sm text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  All Cities
                </button>
                {cities.map((c) => (
                  <button
                    key={c}
                    onClick={() => { onCityChange(c); setOpenDropdown(null); }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      cityFilter === c ? "bg-primary-50 text-primary-700 font-medium" : "text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {(planFilter || statusFilter || cityFilter || search) && (
            <button
              onClick={() => {
                onSearchChange("");
                onPlanChange("");
                onStatusChange("");
                onCityChange("");
              }}
              className="px-3 py-2.5 text-xs font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-200/50 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 text-[11px] text-foreground-400">
        <span>Showing <strong className="text-foreground-700">{filteredCount}</strong> of <strong className="text-foreground-700">{totalResults}</strong> tenants</span>
        {(planFilter || statusFilter || cityFilter || search) && (
          <span>&middot; Filtered</span>
        )}
      </div>
    </div>
  );
}